import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BookOpen, Loader2, Search, UserPlus } from 'lucide-react';
import { mediaUrl } from '@/lib/mediaUrl';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OrgAssignmentsTable } from './OrgAssignmentsTable';

const AssignDialog = ({ course, members, onClose, onAssigned }) => {
  const { api } = useAuth();
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dueDate, setDueDate] = useState('');
  useEffect(() => { setSelected([]); setDueDate(''); }, [course]);
  if (!course) return null;
  const learners = members.filter(m => m.role !== 'org_admin' || members.length === 1);
  const toggle = (id) => setSelected(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const assign = async () => {
    setSaving(true);
    try {
      const r = await api.post('/organisations/team/assign-course', { course_id: course.id, user_ids: selected, due_date: dueDate || null });
      toast.success(`Assigned to ${r.data.assigned} staff${r.data.already_enrolled ? ` (${r.data.already_enrolled} already enrolled${r.data.due_date_updated ? `, due date updated for ${r.data.due_date_updated}` : ''})` : ''}`);
      onAssigned();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not assign course');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!course} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col" data-testid="assign-course-dialog">
        <DialogHeader>
          <DialogTitle>Assign course</DialogTitle>
          <DialogDescription>{course.title}</DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm px-1">
          <input type="checkbox" className="accent-[#20acf6]" checked={selected.length === learners.length && learners.length > 0}
            onChange={() => setSelected(selected.length === learners.length ? [] : learners.map(m => m.id))} data-testid="assign-select-all" />
          Select all staff
        </label>
        <div className="flex-1 overflow-y-auto border rounded-lg divide-y min-h-[160px]">
          {learners.map(m => (
            <label key={m.id} className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50">
              <input type="checkbox" className="accent-[#20acf6]" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} data-testid={`assign-member-${m.id}`} />
              <span className="flex-1">{m.first_name} {m.last_name} <span className="text-xs text-slate-400">{m.email}</span></span>
            </label>
          ))}
          {!learners.length && <p className="p-4 text-sm text-slate-500 text-center">No staff yet — invite them from Team Members.</p>}
        </div>
        <label className="text-sm px-1 space-y-1.5 block">
          <span className="font-medium text-slate-700">Due date <span className="text-slate-400 font-normal">(optional)</span></span>
          <Input type="date" value={dueDate} min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)} onChange={(e) => setDueDate(e.target.value)} data-testid="assign-due-date" />
          <span className="text-xs text-slate-400 block">Staff get reminders 7 days and 1 day before; you're emailed if it becomes overdue.</span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={assign} disabled={!selected.length || saving} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="assign-confirm-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Assign to {selected.length || ''} staff
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function OrgCoursesPage() {
  const { api } = useAuth();
  const [courses, setCourses] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [assignCourse, setAssignCourse] = useState(null);
  const [assignTick, setAssignTick] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/courses', { params: { limit: 100 } }).catch(() => ({ data: [] })),
      api.get('/organisations/team/members').catch(() => ({ data: [] })),
    ]).then(([c, m]) => {
      setCourses(Array.isArray(c.data) ? c.data : (c.data?.courses || []));
      setMembers(m.data || []);
    }).finally(() => setLoading(false));
  }, [api]);

  const list = courses.filter(c => !q || c.title?.toLowerCase().includes(q.toLowerCase()));
  return (
    <DashboardLayout requiredRoles={['org_admin']}>
      <div className="space-y-6" data-testid="org-courses-page">
        <div>
          <h1 className="text-2xl font-bold text-[#2a3e5d]">Team Courses</h1>
          <p className="text-sm text-slate-500 mt-1">Assign courses to specific staff. Track their progress and certificates in Reports.</p>
        </div>
        <Tabs defaultValue="courses">
          <TabsList>
            <TabsTrigger value="courses" data-testid="org-tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="org-tab-assignments">Assignments &amp; due dates</TabsTrigger>
          </TabsList>
          <TabsContent value="courses" className="space-y-6 mt-4">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses..." className="pl-9" data-testid="org-course-search" />
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map(c => (
              <Card key={c.id} className="border-slate-200 overflow-hidden" data-testid={`org-course-${c.id}`}>
                {c.thumbnail_url
                  ? <img src={mediaUrl(c.thumbnail_url)} alt={c.thumbnail_alt || c.title} className="h-32 w-full object-cover" />
                  : <div className="h-32 bg-slate-100 flex items-center justify-center"><BookOpen className="h-8 w-8 text-slate-300" /></div>}
                <CardContent className="p-4 space-y-3">
                  <p className="font-semibold text-sm text-[#2a3e5d] line-clamp-2">{c.title}</p>
                  <Button size="sm" onClick={() => setAssignCourse(c)} className="w-full bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid={`assign-course-btn-${c.id}`}>
                    <UserPlus className="h-4 w-4 mr-2" /> Assign to staff
                  </Button>
                </CardContent>
              </Card>
            ))}
            {!list.length && <p className="text-sm text-slate-500">No courses found.</p>}
          </div>
        )}
          </TabsContent>
          <TabsContent value="assignments" className="mt-4">
            <OrgAssignmentsTable refreshKey={assignTick} />
          </TabsContent>
        </Tabs>
      </div>
      <AssignDialog course={assignCourse} members={members} onClose={() => setAssignCourse(null)} onAssigned={() => setAssignTick(t => t + 1)} />
    </DashboardLayout>
  );
}
